import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AdminSectionCard } from '@/components/admin/AdminSectionCard';
import { BlockField } from '@/components/blog/BlockField';
import {
  applyFactValues,
  applyFieldValues,
  describeBody,
  describePathSlugLock,
  fieldNameForPath,
  flattenFactValues,
  flattenFields,
  INVARIANT_REASON,
} from '@/lib/blog/editor-model';
import { buildLocaleFormDefaults } from '@/lib/blog/form-defaults';
import { contentFieldAttrs, LTR_ISOLATE_ATTRS } from '@/lib/blog/locale-options';
import {
  blogLocaleFormSchema,
  type BlogLocaleFormInput,
  type BlogLocaleFormOutput,
} from '@/lib/schemas/blog-schemas';
import type {
  Block,
  BlogLocaleCopy,
  BlogLocaleEntry,
  BlogValidationIssue,
  FactDefinition,
} from '@/lib/blog/types';

export interface LocaleEditorSubmission {
  values: BlogLocaleFormOutput;
  body: Block[];
  /** Keyed by fact id, not by the synthetic control name the form registers. */
  factValues: Record<string, string>;
}

interface LocaleEditorFormProps {
  copy: BlogLocaleCopy;
  entry: BlogLocaleEntry;
  /**
   * The cluster's fact definitions (AM-18).
   *
   * A prop rather than something read off the copy: the form owes a row to
   * every fact the post defines, and the copy carries only the ones this
   * language has already written.
   */
  facts: FactDefinition[];
  /** Server-named issues, rendered on the field they name (V16). */
  issues: BlogValidationIssue[];
  saving: boolean;
  /** Typed work restored after a locale switch (AM-15.2). */
  cachedValues?: BlogLocaleFormInput | undefined;
  /** Called on unmount with whatever is in the form, dirty or not. */
  onCacheValues: (values: BlogLocaleFormInput, dirty: boolean) => void;
  onSubmit: (submission: LocaleEditorSubmission) => void;
}

export function LocaleEditorForm({
  copy,
  entry,
  facts,
  issues,
  saving,
  cachedValues,
  onCacheValues,
  onSubmit,
}: LocaleEditorFormProps) {
  const groups = useMemo(() => describeBody(copy.body), [copy.body]);
  const flat = useMemo(() => flattenFields(groups), [groups]);
  const factFields = useMemo(
    () => flattenFactValues(facts, copy.factValues),
    [facts, copy.factValues],
  );
  const lock = describePathSlugLock(entry);
  const attrs = contentFieldAttrs(copy.locale);

  // Zod 4 `.pipe()`/transform means input and output types differ, so the form
  // takes all three generics (same shape as CampaignManager).
  const form = useForm<BlogLocaleFormInput, unknown, BlogLocaleFormOutput>({
    resolver: zodResolver(blogLocaleFormSchema),
    defaultValues: cachedValues ?? buildLocaleFormDefaults(copy, facts),
  });

  /**
   * Read during render on purpose: react-hook-form's `formState` is a proxy
   * that only starts maintaining a flag once something has read it while
   * rendering. Reading `isDirty` for the first time inside the unmount effect
   * would return a value nothing ever updated.
   */
  const isDirty = form.formState.isDirty;

  /**
   * Hand whatever is typed to the cache when this form goes away.
   *
   * What the flush needs is held in a ref, refreshed after every render, so the
   * unmount effect can stay dependency-free: a dependency array here would tear
   * down and rebuild the effect on every keystroke, and the cleanup would fire
   * on each one rather than once, when the locale actually changes. The ref
   * carries the values rather than a closure so it has no unreachable default —
   * the seed below is the one the first render would have used anyway.
   */
  const latest = useRef({ onCacheValues, form, isDirty });
  useEffect(() => {
    latest.current = { onCacheValues, form, isDirty };
  });
  useEffect(() => () => {
    const { onCacheValues: cache, form: current, isDirty: dirty } = latest.current;
    cache(current.getValues(), dirty);
  }, []);

  const issueFor = (path: string): string | undefined =>
    issues.find((issue) => issue.path === path)?.message;

  const unmappedIssues = issues.filter(
    (issue) => fieldNameForPath(flat, factFields, issue.path) === undefined,
  );

  const submit = form.handleSubmit((values) => {
    onSubmit({
      values,
      body: applyFieldValues(copy.body, flat, values.fields),
      factValues: applyFactValues(factFields, values.factValues),
    });
  });

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-6" noValidate>
      <AdminSectionCard title="Metadata" description={`The ${copy.locale} copy, version ${String(copy.version)}.`}>
        <div className="grid gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="blog-title">Title</Label>
            <Input id="blog-title" dir={attrs.dir} lang={attrs.lang} {...form.register('title')} />
            {form.formState.errors.title !== undefined && (
              <p className="text-xs text-destructive" role="alert">
                {form.formState.errors.title.message}
              </p>
            )}
            {issueFor('title') !== undefined && (
              <p className="text-xs text-destructive" role="alert">
                {issueFor('title')}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="blog-description">Description</Label>
            <Textarea
              id="blog-description"
              dir={attrs.dir}
              lang={attrs.lang}
              {...form.register('description')}
            />
            {form.formState.errors.description !== undefined && (
              <p className="text-xs text-destructive" role="alert">
                {form.formState.errors.description.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="blog-excerpt">Excerpt</Label>
            <Textarea
              id="blog-excerpt"
              dir={attrs.dir}
              lang={attrs.lang}
              {...form.register('excerpt')}
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="blog-path-slug">URL path for this locale</Label>
            {/* An address is a slug, never prose: it stays LTR and isolated
                whatever the copy around it is written in (AM-15.3). */}
            <Input
              id="blog-path-slug"
              className="font-mono"
              disabled={lock.locked}
              {...LTR_ISOLATE_ATTRS}
              {...form.register('pathSlug')}
            />
            <p className="text-xs text-muted-foreground" data-testid="path-slug-reason">
              {lock.reason}
            </p>
            {form.formState.errors.pathSlug !== undefined && (
              <p className="text-xs text-destructive" role="alert">
                {form.formState.errors.pathSlug.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="blog-keywords">Keywords for this language</Label>
            {/* Per locale since AM-18: the copies never agreed on these
                and were never meant to — a French reader searches for
                "importer un CV", not for "resume import". */}
            <Textarea
              id="blog-keywords"
              dir={attrs.dir}
              lang={attrs.lang}
              {...form.register('keywords')}
            />
            <p className="text-xs text-muted-foreground">
              One per line, in this language. Content-planning terms only — they are never emitted
              as a keywords meta tag.
            </p>
            {issueFor('keywords') !== undefined && (
              <p className="text-xs text-destructive" role="alert">
                {issueFor('keywords')}
              </p>
            )}
          </div>
        </div>
      </AdminSectionCard>

      <AdminSectionCard
        title="Fact values in this language"
        description="A fact’s id and the place it was verified are the same in every copy. How the number reads is not — write it the way this language writes it, and never paste the English."
      >
        <div className="grid gap-4">
          {factFields.map((fact) => {
            const name: `factValues.${string}` = `factValues.${fact.key}`;
            const issue = issueFor(`factValues.${fact.id}`);
            return (
              <div key={fact.id} className="grid gap-2 rounded-2xl border p-3">
                {/* Read-only context, in the same shape the block invariants
                    use: a translator needs to know what the number means and
                    where it came from, and needs to be unable to change it. */}
                <dl className="grid gap-1 text-xs text-muted-foreground">
                  <div className="flex gap-2">
                    <dt>Token</dt>
                    <dd className="font-mono" {...LTR_ISOLATE_ATTRS}>
                      {fact.token}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt>Verified at</dt>
                    <dd className="font-mono" {...LTR_ISOLATE_ATTRS}>
                      {fact.source}
                    </dd>
                  </div>
                  <p>{INVARIANT_REASON}</p>
                </dl>
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`blog-fact-${fact.key}`}>Value</Label>
                  <Input
                    id={`blog-fact-${fact.key}`}
                    dir={attrs.dir}
                    lang={attrs.lang}
                    {...form.register(name)}
                  />
                  {!fact.written && (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid={`fact-unwritten-${fact.id}`}
                    >
                      Not written in this language yet. Until it is, this post cannot publish.
                    </p>
                  )}
                  {issue !== undefined && (
                    <p className="text-xs text-destructive" role="alert">
                      {issue}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {factFields.length === 0 && (
            <p className="text-sm text-muted-foreground">This post declares no facts.</p>
          )}
        </div>
      </AdminSectionCard>

      <AdminSectionCard
        title="Glossary"
        description="Presence is checked against the terms English defines. Nothing here claims the translation is right — only that a definition exists."
      >
        <div className="grid gap-4">
          {copy.glossary.map((term, index) => {
            const name: `glossary.${number}.definition` = `glossary.${index}.definition`;
            return (
              <div key={term.term} className="flex flex-col gap-1">
                <Label htmlFor={`blog-gloss-${String(index)}`}>
                  <span className="font-mono" {...LTR_ISOLATE_ATTRS}>
                    {term.term}
                  </span>
                </Label>
                <Textarea
                  id={`blog-gloss-${String(index)}`}
                  dir={attrs.dir}
                  lang={attrs.lang}
                  {...form.register(name)}
                />
              </div>
            );
          })}
          {copy.glossary.length === 0 && (
            <p className="text-sm text-muted-foreground">This post defines no glossary terms.</p>
          )}
        </div>
      </AdminSectionCard>

      <AdminSectionCard title="Body" description={`${String(groups.length)} blocks. The structure is fixed — blocks cannot be added, removed or reordered here.`}>
        <div className="grid gap-6">
          {groups.map((group) => (
            <fieldset key={group.index} className="rounded-2xl border p-4">
              <legend className="px-1 text-sm font-semibold">{group.heading}</legend>
              {group.invariants.length > 0 && (
                <dl className="mb-3 grid gap-1 text-xs text-muted-foreground">
                  {group.invariants.map((invariant) => (
                    <div key={invariant.label} className="flex gap-2">
                      <dt>{invariant.label}</dt>
                      <dd className="font-mono" {...LTR_ISOLATE_ATTRS}>
                        {invariant.value}
                      </dd>
                    </div>
                  ))}
                  <p>{INVARIANT_REASON}</p>
                </dl>
              )}
              <div className="grid gap-3">
                {/* Driven off the flattened list rather than the group's own
                    fields: the two are built from the same body in the same
                    render, so looking one up in the other could only ever
                    succeed — and a lookup that cannot fail should not be
                    written as one that can. */}
                {flat
                  .filter((field) => field.groupIndex === group.index)
                  .map((field) => {
                    const name: `fields.${string}` = `fields.${field.key}`;
                    return (
                      <BlockField
                        key={field.path}
                        id={`blog-${field.key}`}
                        label={field.label}
                        control={field.control}
                        locale={copy.locale}
                        error={issueFor(field.path)}
                        registration={form.register(name)}
                      />
                    );
                  })}
              </div>
            </fieldset>
          ))}
        </div>
      </AdminSectionCard>

      {unmappedIssues.length > 0 && (
        <div className="rounded-2xl border border-destructive p-4" data-testid="unmapped-issues">
          <h4 className="text-sm font-semibold">Issues that do not belong to a field on this form</h4>
          <ul className="mt-2 grid gap-1 text-sm text-muted-foreground">
            {unmappedIssues.map((issue) => (
              <li key={`${issue.path}:${issue.code}`}>
                <span className="font-mono" {...LTR_ISOLATE_ATTRS}>
                  {issue.path}
                </span>{' '}
                — {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save this copy'}
        </Button>
        <p className="text-sm text-muted-foreground">
          Saving updates the head and marks unpublished changes. It does not publish anything.
        </p>
      </div>
    </form>
  );
}
